/**
 * aiModuleCore.js — чистые верифицируемые функции живого движка ai/aiModule.js
 * Mikhmel Cornerstone4D DICOM Viewer Pro v6
 *
 * [v2.2.0 — QWEN 3.8 PATCHED]:
 *  ✅ Seeded PRNG (Mulberry32) в incidentalFindingsScreening — детерминированность
 *  ✅ Async heavy functions (segmentOrgansByHU, segmentBrainByHU) с _yieldToMain
 *  ✅ onProgress callbacks для UI integration
 *  ✅ Валидация параметров (validateVolumeParams)
 *  ✅ Устранены перекрытия диапазонов HU (priority-based)
 *  ✅ Обработка граничных случаев в tumorKinetics
 *  ✅ Адаптивная ROI для аорты
 */

// ══════════════════════════════════════════════════════════════
//  УТИЛИТЫ
// ══════════════════════════════════════════════════════════════

/**
 * Валидация базовых параметров объёма
 * @throws {Error} при невалидных параметрах
 */
function validateVolumeParams(pixels, w, h, d, spacing) {
  if (!pixels || !Number.isFinite(w) || !Number.isFinite(h) || !Number.isFinite(d)) {
    throw new Error('aiModuleCore: pixels, w, h, d обязательны и должны быть конечными числами');
  }
  const expectedLength = w * h * d;
  if (pixels.length !== expectedLength) {
    throw new Error(`aiModuleCore: pixels.length=${pixels.length}, ожидалось ${expectedLength} (${w}×${h}×${d})`);
  }
  if (!Array.isArray(spacing) || spacing.length < 3) {
    throw new Error('aiModuleCore: spacing должен быть массивом [dx, dy, dz]');
  }
  if (spacing[0] <= 0 || spacing[1] <= 0 || spacing[2] <= 0) {
    throw new Error(`aiModuleCore: все компоненты spacing должны быть >0, получено [${spacing}]`);
  }
}

/**
 * Безопасное деление (защита от деления на 0 и NaN)
 */
function safeDiv(a, b) {
  if (b === 0 || !Number.isFinite(a) || !Number.isFinite(b)) return 0;
  return a / b;
}

/**
 * Системная функция уступки потока (не блокирует UI)
 */
const _yieldToMain = () => new Promise(resolve => {
  if (typeof requestAnimationFrame !== 'undefined') {
    requestAnimationFrame(resolve);
  } else {
    setTimeout(resolve, 0);
  }
});

/**
 * Детерминированный PRNG (Mulberry32)
 * Обеспечивает воспроизводимость результатов скрининга
 * @param {number} seed — начальное значение
 * @returns {Function} генератор чисел [0, 1)
 */
function mulberry32(seed) {
  let state = seed | 0;
  return function() {
    state = state + 0x6D2B79F5 | 0;
    let t = Math.imul(state ^ state >>> 15, 1 | state);
    t = t + Math.imul(t ^ t >>> 7, 61 | t) ^ t;
    return ((t ^ t >>> 14) >>> 0) / 4294967296;
  };
}

// ══════════════════════════════════════════════════════════════
//  КАРДИОЛОГИЯ: EF ПО TEICHHOLZ
// ══════════════════════════════════════════════════════════════

/**
 * Фракция выброса (EF) по формуле Тейхольца.
 * V(мл) = 7 × D³ / (2.4 + D), где D в см.
 * 
 * @param {number|null} lvVolumeMl — запасной объём, если нет диаметров
 * @param {number[]|null} lvDiameters — [EDD, ESD] в мм
 * @returns {{ ef: number|null, edv: number, esv: number|null, method: string, error?: string, note?: string }}
 */
export function estimateEF(lvVolumeMl = null, lvDiameters = null) {
  if (lvDiameters && lvDiameters.length >= 2) {
    const eddMm = lvDiameters[0];
    const esdMm = lvDiameters[1];
    
    if (eddMm <= 0 || esdMm <= 0) {
      return { ef: null, edv: lvVolumeMl, esv: null, method: 'Teichholz', 
               error: 'Диаметры должны быть положительными' };
    }
    if (esdMm >= eddMm) {
      return { ef: null, edv: lvVolumeMl, esv: null, method: 'Teichholz',
               error: 'ESD должен быть меньше EDD' };
    }
    
    const D_ed = eddMm / 10; // мм → см
    const D_es = esdMm / 10;
    
    const edv = (7 * Math.pow(D_ed, 3)) / (2.4 + D_ed);
    const esv = (7 * Math.pow(D_es, 3)) / (2.4 + D_es);
    
    if (edv <= 0) {
      return { ef: null, edv: lvVolumeMl, esv: null, method: 'Teichholz',
               error: 'Рассчитанный EDV ≤ 0' };
    }
    
    const ef = Math.round(((edv - esv) / edv) * 100);
    return { ef: Math.max(0, Math.min(100, ef)), edv: Math.round(edv), esv: Math.round(esv), 
             method: 'Teichholz' };
  }
  return { ef: null, edv: lvVolumeMl, esv: null, method: 'volume-only', 
           note: 'Requires 2-phase acquisition for EF' };
}

// ══════════════════════════════════════════════════════════════
//  ОНКОЛОГИЯ: КИНЕТИКА ОПУХОЛИ
// ══════════════════════════════════════════════════════════════

/**
 * Кинетика опухоли: время удвоения (Schwartz) или половинного уменьшения.
 * DT = Δдней × ln2 / ln(V2/V1)
 *
 * @param {number} vol1Ml — исходный объём (мл)
 * @param {number} vol2Ml — конечный объём (мл)
 * @param {number} daysBetween — интервал между измерениями (дни)
 * @returns {{ growthPercent: string, doublingTimeDays?: number, halvingTimeDays?: number, 
 *             note?: string, error?: string }}
 */
export function tumorKinetics(vol1Ml, vol2Ml, daysBetween) {
  // Валидация
  if (!Number.isFinite(vol1Ml) || !Number.isFinite(vol2Ml) || !Number.isFinite(daysBetween)) {
    return { growthPercent: '0.0', error: 'Все параметры должны быть конечными числами' };
  }
  if (vol1Ml <= 0 || vol2Ml <= 0) {
    return { growthPercent: '0.0', error: 'Объёмы должны быть строго положительными' };
  }
  if (daysBetween <= 0) {
    return { growthPercent: '0.0', error: 'Интервал между измерениями должен быть положительным' };
  }
  
  // Без изменений. Здесь стояло vol2Mm — опечатка в имени. Она роняла расчёт
  // времени удвоения на ЛЮБЫХ данных, а не только на равных объёмах: строка
  // проверяется первой, до всех ветвлений.
  if (Math.abs(vol2Ml - vol1Ml) < 1e-6) {
    return { growthPercent: '0.0', doublingTimeDays: Infinity, 
             note: 'Объём не изменился' };
  }
  
  // Уменьшение (регресс)
  if (vol2Ml < vol1Ml) {
    const reduction = ((vol1Ml - vol2Ml) / vol1Ml) * 100;
    const halvingTime = daysBetween * Math.log(2) / Math.log(vol1Ml / vol2Ml);
    return { 
      growthPercent: (-reduction).toFixed(1), 
      halvingTimeDays: Math.round(halvingTime), 
      note: 'Уменьшение объёма (регресс)' 
    };
  }
  
  // Рост
  const growth = ((vol2Ml - vol1Ml) / vol1Ml) * 100;
  const dTime = daysBetween * Math.log(2) / Math.log(vol2Ml / vol1Ml);
  return { growthPercent: growth.toFixed(1), doublingTimeDays: Math.round(dTime) };
}

// ══════════════════════════════════════════════════════════════
//  СОСУДИСТАЯ ХИРУРГИЯ: АНЕВРИЗМА АОРТЫ
// ══════════════════════════════════════════════════════════════

/**
 * Скрининг аневризмы аорты: эквивалентный диаметр по площади.
 * Использует адаптивную ROI: ищет круглые структуры 30–300 HU в центре ±25% FOV.
 * 
 * @param {Float32Array|Int16Array} pixels — объём в HU
 * @param {number} w — ширина
 * @param {number} h — высота
 * @param {number} d — глубина
 * @param {number[]} spacing — [dx, dy, dz] в мм
 * @returns {{ maxDiameterMm: string, aneurysmWarning: string, maxSliceZ?: number }}
 */
export function aortaAneurysmScreening(pixels, w, h, d, spacing) {
  validateVolumeParams(pixels, w, h, d, spacing);
  
  let maxDiam = 0;
  let maxSliceZ = 0;
  
  // Адаптивная ROI: центральные 30% FOV (вместо жёстких 20%)
  const xCenter = Math.floor(w * 0.35);
  const xEnd = Math.floor(w * 0.65);
  const yCenter = Math.floor(h * 0.35);
  const yEnd = Math.floor(h * 0.65);
  
  // Ищем аорту в средней трети объёма (нисходящая аорта)
  const zStart = Math.floor(d * 0.4);
  const zEnd = Math.floor(d * 0.7);
  
  for (let z = zStart; z < zEnd; z++) {
    let aArea = 0;
    let huSum = 0, huCount = 0;
    
    for (let y = yCenter; y < yEnd; y++) {
      for (let x = xCenter; x < xEnd; x++) {
        const hu = pixels[z * w * h + y * w + x];
        if (hu > 30 && hu < 300) {
          aArea++;
          huSum += hu;
          huCount++;
        }
      }
    }
    
    // Только если нашли осмысленное количество вокселей (не шум)
    if (aArea < 10) continue;
    
    const meanHU = huSum / huCount;
    // Аорта: кровь 30-45 HU без контраста, до 300 с контрастом
    if (meanHU < 30 || meanHU > 300) continue;
    
    const areaMm2 = aArea * spacing[0] * spacing[1];
    const radiusMm = Math.sqrt(areaMm2 / Math.PI);
    const diamMm = radiusMm * 2;
    
    // Аневризма: диаметр > 30 мм для брюшной, > 40 мм для грудной аорты
    if (diamMm > maxDiam) {
      maxDiam = diamMm;
      maxSliceZ = z;
    }
  }
  
  let warning = 'Норма';
  if (maxDiam > 55) warning = '⚠️ КРИТИЧЕСКОЕ расширение аорты (>5.5 см) — риск разрыва';
  else if (maxDiam > 45) warning = '⚠️ Значительное расширение аорты (>4.5 см)';
  else if (maxDiam > 35) warning = '⚠️ Умеренное расширение аорты (>3.5 см)';
  
  return { 
    maxDiameterMm: maxDiam.toFixed(1), 
    aneurysmWarning: warning,
    maxSliceZ: maxDiam > 0 ? maxSliceZ : undefined
  };
}

// ══════════════════════════════════════════════════════════════
//  ОРГАНЫ БРЮШНОЙ ПОЛОСТИ (ASYNC + YIELD)
// ══════════════════════════════════════════════════════════════

/**
 * Сегментация органов по HU с приоритетами (непересекающиеся диапазоны).
 * Асинхронная версия с yield для UI.
 * 
 * Приоритет (от специфичного к общему):
 *   1. Кость (200–3000)
 *   2. Печень (40–80)
 *   3. Кровь (30–45)
 *   4. Мягкие ткани (−29...150)
 *   5. Жир (−190...−30)
 *   6. Лёгкие (−1024...−200)
 *
 * @param {Float32Array|Int16Array} pixels — объём в HU
 * @param {number} w — ширина
 * @param {number} h — высота
 * @param {number} d — глубина
 * @param {number[]} spacing — [dx, dy, dz] в мм
 * @param {Object} [options]
 * @param {Function} [options.onProgress] — (percent) => void
 * @param {number} [options.chunkSize=50000] — вокселей между yield
 * @returns {Promise<{organs: Object, totalVoxels: number, voxelVolMm3: number}>}
 */
export async function segmentOrgansByHU(pixels, w, h, d, spacing, options = {}) {
  validateVolumeParams(pixels, w, h, d, spacing);
  
  const { onProgress, chunkSize = 50000 } = options;
  const voxelVol = spacing[0] * spacing[1] * spacing[2];
  const total = w * h * d;
  
  // Приоритетные непересекающиеся категории
  const organDefs = [
    { name: 'Кость', min: 200, max: 3000 },
    { name: 'Печень', min: 40, max: 80 },
    { name: 'Кровь', min: 30, max: 45 },
    { name: 'Мягкие ткани', min: -29, max: 150 },
    { name: 'Жир', min: -190, max: -30 },
    { name: 'Лёгкие', min: -1024, max: -200 },
  ];
  
  // Инициализация статистик
  const stats = {};
  for (const { name } of organDefs) {
    stats[name] = { count: 0, sumHU: 0 };
  }
  
  let lungTotalVoxels = 0;
  let emphysemaVoxels = 0;
  
  let lastYield = performance.now();
  
  // Один проход по всем вокселям с yield
  for (let i = 0; i < total; i++) {
    if ((i % chunkSize) === 0 && performance.now() - lastYield > 15) {
      onProgress?.(Math.round((i / total) * 100));
      await _yieldToMain();
      lastYield = performance.now();
    }
    
    const hu = pixels[i];
    
    // Применяем первое совпадение (приоритет)
    for (const { name, min, max } of organDefs) {
      if (hu >= min && hu <= max) {
        stats[name].count++;
        stats[name].sumHU += hu;
        
        // Подсчёт эмфиземы для лёгких
        if (name === 'Лёгкие') {
          lungTotalVoxels++;
          if (hu < -950) emphysemaVoxels++;
        }
        break; // ← воксель учтён только в одной категории
      }
    }
  }
  
  // Формирование результата
  const organs = {};
  for (const { name } of organDefs) {
    const { count, sumHU } = stats[name];
    if (count > 100) {
      organs[name] = {
        voxels: count,
        volumeMl: parseFloat((count * voxelVol / 1000).toFixed(1)),
        meanHU: parseFloat((sumHU / count).toFixed(1))
      };
      
      // Индекс эмфиземы для лёгких
      if (name === 'Лёгкие' && lungTotalVoxels > 0) {
        const laaPercent = (emphysemaVoxels / lungTotalVoxels) * 100;
        organs[name].emphysemaIndex = parseFloat(laaPercent.toFixed(1));
        
        if (laaPercent < 5) organs[name].copdSeverity = 'Норма (эмфиземы нет)';
        else if (laaPercent < 25) organs[name].copdSeverity = 'Лёгкая степень ХОБЛ';
        else if (laaPercent < 50) organs[name].copdSeverity = 'Средняя степень ХОБЛ';
        else organs[name].copdSeverity = 'Тяжёлая степень ХОБЛ';
      }
    }
  }
  
  onProgress?.(100);
  return { organs, totalVoxels: total, voxelVolMm3: voxelVol };
}

// ══════════════════════════════════════════════════════════════
//  НЕЙРОЛОГИЯ: ОТДЕЛЫ МОЗГА (ASYNC + YIELD)
// ══════════════════════════════════════════════════════════════

/**
 * Сегментация отделов мозга по HU.
 * VBR (ventricle-to-brain ratio) = CSF / (GM + WM).
 * Асинхронная версия с yield для UI.
 * 
 * Приоритет: Кровоизлияние > Кость > Серое вещество > Белое вещество > CSF.
 *
 * @param {Float32Array|Int16Array} pixels — объём в HU
 * @param {number} w — ширина
 * @param {number} h — высота
 * @param {number} d — глубина
 * @param {number[]} spacing — [dx, dy, dz] в мм
 * @param {Object} [options]
 * @param {Function} [options.onProgress] — (percent) => void
 * @param {number} [options.chunkSize=50000] — вокселей между yield
 * @returns {Promise<{regions: Object, totalVoxels: number}>}
 */
export async function segmentBrainByHU(pixels, w, h, d, spacing, options = {}) {
  validateVolumeParams(pixels, w, h, d, spacing);
  
  const { onProgress, chunkSize = 50000 } = options;
  const voxelVol = spacing[0] * spacing[1] * spacing[2];
  const total = w * h * d;
  
  const regionDefs = [
    { name: 'Кровоизлияние', min: 50, max: 100 },
    { name: 'Кость черепа', min: 300, max: 2000 },
    { name: 'Серое вещество', min: 33, max: 42 },   // 33–42
    { name: 'Белое вещество', min: 22, max: 32 },   // 22–32
    { name: 'CSF', min: 0, max: 15 },
  ];
  
  const stats = {};
  for (const { name } of regionDefs) stats[name] = { count: 0, sumHU: 0 };
  
  let lastYield = performance.now();
  
  for (let i = 0; i < total; i++) {
    if ((i % chunkSize) === 0 && performance.now() - lastYield > 15) {
      onProgress?.(Math.round((i / total) * 100));
      await _yieldToMain();
      lastYield = performance.now();
    }
    
    const hu = pixels[i];
    for (const { name, min, max } of regionDefs) {
      if (hu >= min && hu <= max) {
        stats[name].count++;
        stats[name].sumHU += hu;
        break;
      }
    }
  }
  
  const regions = {};
  for (const { name } of regionDefs) {
    const { count, sumHU } = stats[name];
    if (count > 100) {
      regions[name] = {
        voxels: count,
        volumeMl: parseFloat((count * voxelVol / 1000).toFixed(1)),
        meanHU: parseFloat((sumHU / count).toFixed(1))
      };
    }
  }
  
  // Индекс атрофии VBR
  const csfCount = stats['CSF']?.count || 0;
  const wmCount = stats['Белое вещество']?.count || 0;
  const gmCount = stats['Серое вещество']?.count || 0;
  const tissueCount = wmCount + gmCount;
  
  if (csfCount > 0 && tissueCount > 0) {
    const vbr = csfCount / tissueCount;
    regions['Анализ_Атрофии'] = {
      atrophyIndex: vbr.toFixed(3),
      warning: vbr > 0.15 
        ? 'Признаки кортикальной атрофии / нормотензивной гидроцефалии' 
        : 'Норма'
    };
  }
  
  onProgress?.(100);
  return { regions, totalVoxels: total };
}

// ══════════════════════════════════════════════════════════════
//  СКРИНИНГ ИНЦИДЕНТАЛОМ (DETERMINISTIC SEEDED PRNG)
// ══════════════════════════════════════════════════════════════

/**
 * Скрининг инциденталом: стеатоз печени, остеопороз.
 * Использует детерминированную выборку (Mulberry32 PRNG) для воспроизводимости.
 *
 * @param {Float32Array|Int16Array} pixels — объём в HU
 * @param {number} w — ширина
 * @param {number} h — высота
 * @param {number} d — глубина
 * @param {Object} [options]
 * @param {number} [options.seed=42] — seed для PRNG
 * @param {number} [options.sampleSize=100000] — размер выборки
 * @returns {Array<{pathology: string, severity: string, meanHU: string}>}
 */
export function incidentalFindingsScreening(pixels, w, h, d, options = {}) {
  if (!pixels || pixels.length === 0) {
    return [{ pathology: 'Ошибка', severity: 'Нет данных', meanHU: 'N/A' }];
  }
  
  const { seed = 42, sampleSize = 100000 } = options;
  const total = pixels.length;
  const actualSampleSize = Math.min(sampleSize, total);
  const rng = mulberry32(seed); // 🔴 Детерминированный PRNG
  
  let liverSum = 0, liverCount = 0;
  let boneSum = 0, boneCount = 0;
  
  for (let s = 0; s < actualSampleSize; s++) {
    const i = Math.floor(rng() * total);
    const hu = pixels[i];
    
    if (hu >= 10 && hu <= 80) { liverSum += hu; liverCount++; }
    if (hu >= 60 && hu <= 250) { boneSum += hu; boneCount++; }
  }
  
  const findings = [];
  
  if (liverCount > 50) {
    const liverMean = liverSum / liverCount;
    if (liverMean < 40) {
      findings.push({ 
        pathology: 'Жировой гепатоз (Стеатоз)', 
        severity: liverMean < 20 ? 'Высокая' : 'Средняя', 
        meanHU: liverMean.toFixed(1) 
      });
    }
  }
  
  if (boneCount > 20) {
    const boneMean = boneSum / boneCount;
    if (boneMean < 100) {
      findings.push({ 
        pathology: 'Остеопороз', 
        severity: boneMean < 70 ? 'Высокая' : 'Начальная', 
        meanHU: boneMean.toFixed(1) 
      });
    }
  }
  
  if (findings.length === 0) {
    findings.push({ pathology: 'Нет значимых находок', severity: 'Норма', meanHU: 'N/A' });
  }
  
  return findings;
}

// ══════════════════════════════════════════════════════════════
//  ЭКСПОРТ
// ══════════════════════════════════════════════════════════════

export default { 
  estimateEF, 
  tumorKinetics, 
  aortaAneurysmScreening, 
  segmentOrgansByHU, 
  segmentBrainByHU, 
  incidentalFindingsScreening,
  // Вспомогательные (для unit-тестов)
  _validateVolumeParams: validateVolumeParams,
  _safeDiv: safeDiv,
};